import * as Dialog from '@radix-ui/react-dialog';
import { Trash2 } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { Button } from '@/components/Button/Button';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { isValidEmail } from '@/features/profile/profileService';

import { buildStaffInviteLink, buildStaffInviteMailto } from './staffInviteLinks';
import type { CreatedStaffInvite } from './staffInviteService';
import {
  isStaffInviteExpired,
  staffInviteFullName,
  staffRoleDescription,
  staffRoleLabel,
  type StaffInvite,
  type StaffRole,
} from './staffInviteTypes';
import type { CourseStaffInvitesController } from './useCourseStaffInvites';
import styles from './StaffInvitePanels.module.css';

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message.trim()
    ? error.message
    : 'Something went wrong. Please try again.';
}

export function formatInviteDate(millis: number | null): string {
  return millis === null
    ? 'soon'
    : new Date(millis).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function inviteLink(invite: StaffInvite): string {
  return buildStaffInviteLink(window.location.origin, invite.key);
}

// --- Invite: offer → form → link ---------------------------------------------

interface StaffInviteSectionProps {
  controller: CourseStaffInvitesController;
  courseTitle: string;
  // The searched email that matched no instructor account.
  email: string;
  // From the dialog's "Grant full edit rights" checkbox.
  role: StaffRole;
}

export function StaffInviteSection({
  controller,
  courseTitle,
  email,
  role,
}: StaffInviteSectionProps) {
  const formHeadingId = useId();
  const firstNameId = useId();
  const lastNameId = useId();
  const emailId = useId();
  const [step, setStep] = useState<'offer' | 'form' | 'link'>('offer');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [inviteEmail, setInviteEmail] = useState(email);
  const [errors, setErrors] = useState<Partial<Record<'email' | 'firstName' | 'form' | 'lastName', string>>>({});
  const [created, setCreated] = useState<CreatedStaffInvite | null>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'form') {
      firstNameRef.current?.focus();
    }
  }, [step]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: typeof errors = {};
    const normalizedEmail = inviteEmail.trim().toLowerCase();

    if (!firstName.trim()) {
      nextErrors.firstName = 'Enter their first name.';
    }
    if (!lastName.trim()) {
      nextErrors.lastName = 'Enter their last name.';
    }
    if (!isValidEmail(normalizedEmail)) {
      nextErrors.email = 'Enter a valid email address.';
    }
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    try {
      const result = await controller.createInvite({
        email: normalizedEmail,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role,
      });
      setCreated(result);
      setStep('link');
    } catch (error) {
      setErrors({ form: errorMessage(error) });
    }
  }

  if (step === 'link' && created) {
    return (
      <InviteLinkPanel
        alreadyPending={created.alreadyPending}
        courseTitle={courseTitle}
        invite={created.invite}
      />
    );
  }

  if (step === 'offer') {
    return (
      <div className={styles.offer}>
        <p>
          No instructor account uses <strong>{email}</strong>. You can invite
          them instead: they create an account and join this course.
        </p>
        <Button onClick={() => setStep('form')} size="small" type="button" variant="secondary">
          Invite {email}
        </Button>
      </div>
    );
  }

  const fieldError = (name: 'email' | 'firstName' | 'lastName', id: string) =>
    errors[name] ? (
      <span className={styles.fieldError} id={`${id}-error`}>
        {errors[name]}
      </span>
    ) : null;

  return (
    <form
      aria-labelledby={formHeadingId}
      className={styles.inviteForm}
      noValidate
      onSubmit={(event) => void handleSubmit(event)}
    >
      <h3 className={styles.heading} id={formHeadingId}>
        Invite someone new
      </h3>
      <div className={styles.nameRow}>
        <div className={styles.field}>
          <label htmlFor={firstNameId}>First name</label>
          <input
            aria-describedby={errors.firstName ? `${firstNameId}-error` : undefined}
            aria-invalid={Boolean(errors.firstName)}
            autoComplete="off"
            id={firstNameId}
            onChange={(event) => setFirstName(event.target.value)}
            ref={firstNameRef}
            value={firstName}
          />
          {fieldError('firstName', firstNameId)}
        </div>
        <div className={styles.field}>
          <label htmlFor={lastNameId}>Last name</label>
          <input
            aria-describedby={errors.lastName ? `${lastNameId}-error` : undefined}
            aria-invalid={Boolean(errors.lastName)}
            autoComplete="off"
            id={lastNameId}
            onChange={(event) => setLastName(event.target.value)}
            value={lastName}
          />
          {fieldError('lastName', lastNameId)}
        </div>
      </div>
      <div className={styles.field}>
        <label htmlFor={emailId}>Email</label>
        <input
          aria-describedby={errors.email ? `${emailId}-error` : undefined}
          aria-invalid={Boolean(errors.email)}
          autoComplete="off"
          id={emailId}
          onChange={(event) => setInviteEmail(event.target.value)}
          type="email"
          value={inviteEmail}
        />
        {fieldError('email', emailId)}
      </div>
      <p className={styles.note}>
        They&apos;ll join as {staffRoleDescription(role)}. To change that, use
        &ldquo;Grant full edit rights&rdquo; below. Their Kudu profile is set up
        from these details.
      </p>
      {errors.form ? (
        <p className={styles.error} role="alert">
          {errors.form}
        </p>
      ) : null}
      <div className={styles.actions}>
        <Button disabled={controller.isCreating} size="small" type="submit" variant="primary">
          {controller.isCreating ? 'Creating link…' : 'Create invite link'}
        </Button>
        <Button onClick={() => setStep('offer')} size="small" type="button" variant="ghost">
          Back
        </Button>
      </div>
    </form>
  );
}

// --- The link, once an invite exists ------------------------------------------

export function InviteLinkPanel({
  alreadyPending,
  courseTitle,
  invite,
}: {
  alreadyPending: boolean;
  courseTitle: string;
  invite: StaffInvite;
}) {
  const headingId = useId();
  const linkId = useId();
  const [status, setStatus] = useState('');
  const link = inviteLink(invite);
  const fullName = staffInviteFullName(invite);
  const mailto = buildStaffInviteMailto({
    courseTitle,
    email: invite.email,
    firstName: invite.firstName,
    inviterName: invite.createdByName,
    link,
    role: invite.role,
  });

  async function handleCopy() {
    setStatus(
      (await copyText(link))
        ? 'Link copied'
        : "Couldn't copy the link. Select it and copy it instead.",
    );
  }

  return (
    <section aria-labelledby={headingId} className={styles.linkPanel}>
      <h3 className={styles.heading} id={headingId}>
        {alreadyPending ? 'An invitation is already pending' : 'Invitation ready'}
      </h3>
      <p>
        Send this link to {fullName} ({invite.email}). It works once and expires{' '}
        {formatInviteDate(invite.expiresAt)}.
      </p>
      <div className={styles.field}>
        <label htmlFor={linkId}>Invite link</label>
        <input
          id={linkId}
          onFocus={(event) => event.target.select()}
          readOnly
          value={link}
        />
      </div>
      <p className={styles.warning}>
        Anyone with this link can join as {fullName}. Send it only to them.
      </p>
      <div className={styles.actions}>
        <Button onClick={() => void handleCopy()} size="small" type="button" variant="primary">
          Copy link
        </Button>
        <Button as="a" href={mailto} size="small" variant="secondary">
          Email
        </Button>
      </div>
      <p aria-live="polite" className={styles.status}>
        {status}
      </p>
    </section>
  );
}

// --- Pending invitations -----------------------------------------------------

function PendingInviteRow({
  controller,
  invite,
  now,
  onStatus,
}: {
  controller: CourseStaffInvitesController;
  invite: StaffInvite;
  now: number;
  onStatus(message: string): void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const expired = isStaffInviteExpired(invite, now);
  const fullName = staffInviteFullName(invite);

  useEffect(() => {
    if (confirming) {
      confirmRef.current?.focus();
    }
  }, [confirming]);

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    try {
      await action();
      onStatus(success);
    } catch (error) {
      onStatus(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <span className={styles.rowName}>{fullName}</span>
        <span className={styles.muted}>
          {invite.email} · {staffRoleLabel(invite.role)} ·{' '}
          {expired ? 'Expired' : `Expires ${formatInviteDate(invite.expiresAt)}`}
        </span>
      </div>
      {confirming ? (
        <div className={styles.confirm}>
          <span>Cancel this invitation?</span>
          <Button
            aria-label={`Cancel the invitation for ${fullName}`}
            destructive
            disabled={busy}
            onClick={() => void run(() => controller.cancelInvite(invite.key), 'Invitation cancelled')}
            ref={confirmRef}
            size="small"
            type="button"
            variant="secondary"
          >
            Cancel invitation
          </Button>
          <Button
            aria-label={`Keep the invitation for ${fullName}`}
            onClick={() => setConfirming(false)}
            size="small"
            type="button"
            variant="ghost"
          >
            Keep
          </Button>
        </div>
      ) : (
        <div className={styles.rowActions}>
          {expired ? (
            <Button
              aria-label={`Renew the invitation for ${fullName}`}
              disabled={busy}
              onClick={() => void run(() => controller.renewInvite(invite.key), 'Invitation renewed for 14 days')}
              size="small"
              type="button"
              variant="secondary"
            >
              Renew
            </Button>
          ) : (
            <Button
              aria-label={`Copy link for ${fullName}`}
              onClick={() =>
                void copyText(inviteLink(invite)).then((copied) =>
                  onStatus(copied ? 'Link copied' : "Couldn't copy the link."),
                )
              }
              size="small"
              type="button"
              variant="secondary"
            >
              Copy link
            </Button>
          )}
          <Button
            aria-label={`Cancel the invitation for ${fullName}`}
            onClick={() => setConfirming(true)}
            size="small"
            type="button"
            variant="ghost"
          >
            Cancel
          </Button>
        </div>
      )}
    </li>
  );
}

export function PendingStaffInvites({
  controller,
}: {
  controller: CourseStaffInvitesController;
}) {
  const headingId = useId();
  const [status, setStatus] = useState('');
  const now = Date.now();

  if (controller.invitesError) {
    return <p className={styles.error}>Pending invitations couldn&apos;t be loaded.</p>;
  }

  return (
    <section aria-labelledby={headingId} className={styles.pending}>
      <h4 className={styles.subheading} id={headingId}>
        Pending invitations
      </h4>
      {controller.invites.length > 0 ? (
        <ul className={styles.list}>
          {controller.invites.map((invite) => (
            <PendingInviteRow
              controller={controller}
              invite={invite}
              key={invite.key}
              now={now}
              onStatus={setStatus}
            />
          ))}
        </ul>
      ) : (
        <p className={styles.muted}>
          {controller.isLoadingInvites ? 'Loading invitations…' : 'No pending invitations.'}
        </p>
      )}
      <p aria-live="polite" className={styles.status}>
        {status}
      </p>
    </section>
  );
}

// --- Removing a staff member ---------------------------------------------------

// Who is about to be removed, and from which role.
export interface StaffRemoval {
  memberId: string;
  memberName: string;
  role: StaffRole;
}

// The delete icon beside a staff member. Shown to the course's primary
// instructor only; it opens StaffRemoveConfirmation and removes nobody itself.
export function StaffRemoveButton({
  memberName,
  onClick,
}: {
  memberName: string;
  onClick(): void;
}) {
  return (
    <Tooltip content="Remove from course">
      <button
        aria-label={`Remove ${memberName}`}
        className={styles.removeIcon}
        onClick={onClick}
        type="button"
      >
        <Trash2 aria-hidden="true" size={16} />
      </button>
    </Tooltip>
  );
}

// Asks before a staff member is removed. The repo's confirmation shape (see
// DiscardCoachChangesDialog): the alertdialog role, focus on the safe choice.
//
// The dialog that lists the staff owns this, not the member's row: the row
// leaves the list as the course refreshes, and a pop-up inside it would vanish
// mid-flow and drop focus to the page. On Cancel, focus goes back to the icon
// that opened it (Radix only returns it to a Dialog.Trigger, and this has
// none). After a removal that icon is gone, so the owner says where focus goes.
export function StaffRemoveConfirmation({
  courseTitle,
  focusAfterRemoval,
  onClose,
  onRemove,
  onRemoved,
  removal,
}: {
  courseTitle: string;
  focusAfterRemoval(removed: StaffRemoval): void;
  // Cancel, Escape or a click outside: nobody is removed.
  onClose(): void;
  onRemove(staffUserId: string, role: StaffRole): Promise<void>;
  onRemoved(removed: StaffRemoval): void;
  // Null keeps the pop-up closed.
  removal: StaffRemoval | null;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const cancelRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const removedRef = useRef<StaffRemoval | null>(null);

  // A fresh start for each person asked about.
  useEffect(() => {
    setBusy(false);
    setError('');
  }, [removal]);

  async function handleRemove() {
    if (!removal) {
      return;
    }

    setBusy(true);
    setError('');
    try {
      await onRemove(removal.memberId, removal.role);
    } catch (removeError) {
      setError(errorMessage(removeError));
      setBusy(false);
      return;
    }

    removedRef.current = removal;
    onRemoved(removal);
  }

  return (
    <Dialog.Root
      onOpenChange={(nextOpen) => {
        // Not while the request is out: its answer needs somewhere to land.
        if (!nextOpen && !busy) {
          onClose();
        }
      }}
      open={removal !== null}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.confirmOverlay} />
        <Dialog.Content
          className={styles.confirmDialog}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const removed = removedRef.current;
            removedRef.current = null;

            if (removed) {
              focusAfterRemoval(removed);
            } else if (returnFocusRef.current?.isConnected) {
              returnFocusRef.current.focus();
            }
          }}
          onOpenAutoFocus={(event) => {
            // Focus has not moved yet: this is the icon that opened the pop-up.
            returnFocusRef.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
            event.preventDefault();
            cancelRef.current?.focus();
          }}
          role="alertdialog"
        >
          <div>
            <Dialog.Title className={styles.confirmTitle}>
              Remove {removal?.memberName} from this course?
            </Dialog.Title>
            <Dialog.Description className={styles.confirmText}>
              They will lose their access to {courseTitle} as{' '}
              {removal?.role === 'assistant' ? 'an assistant' : 'an additional instructor'}.
              You can add them again later.
            </Dialog.Description>
          </div>

          {error ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : null}

          <div className={styles.confirmFooter}>
            <Button disabled={busy} onClick={onClose} ref={cancelRef} variant="secondary">
              Cancel
            </Button>
            {/* Outlined, not filled: the filled danger button's white text
                falls short of 4.5:1 in the dark theme. */}
            <Button
              destructive
              disabled={busy}
              onClick={() => void handleRemove()}
              variant="secondary"
            >
              {busy ? 'Removing…' : 'Remove'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
